import Vue, { CreateElement } from 'vue'
import { Component } from 'vue-property-decorator'
import moment from 'moment'

import { Subtitle1, H6, Subtitle2 } from '@src/elements/typography/typography'
import { Icon } from '@src/elements/icon/icon'
import { Spacer } from '@src/elements/spacer/spacer'
import { Button, MenuButton } from '@src/elements/button/button'
import { CombinedProgressBar, ProgressBar } from '@src/elements/progress-bar/progress-bar'
import { HealthGoal, HealthIndicator, HealthIndicatorType } from '@src/data/goal'
import ApiClient from '@src/lib/api_client'
import { TimeSwitcher } from '@src/elements/time-switch/time-switch'
import AddGoal from '@src/routes/members/components/health-goals/add-goal'
import { ConfirmModal } from '@src/elements/modal/confirm-modal'
import { KGITooltip } from '@src/routes/members/components/health-goals/kgi-tooltip'
import EditKgis from '@src/routes/members/components/health-goals/edit-kgis'
import { API } from '@src/lib/api_path'
import Activity from '@src/data/activity'
import { ExerciseTag } from '@src/util/default-values'
import { ContentBox } from '@src/elements/content-box/content-box'
import { ErrorScreen } from '@src/components/error-screen/error-screen'
import { Popover } from '@src/components/popover/popover'
import { OptionGroup } from '@src/elements/select/option-group'
import { NotificationTypes } from '@src/util/constants'
import { HealthGoalsService } from '@src/service/health-goal'
import { convertToYYYYMMDD } from '@src/util/date-format'

import './health-goals.sass'
import { DateFilterType } from '@src/elements/time-switch/date-filter-type'

export type HealthGoalResponse = { health_goal: HealthGoal }

const PROGRESS_COLOR_SEQUENCE = ['#A600FF', '#72ECB2', '#FC297D', '#29D9C1', '#2C6CC9', '#7998F4', '#FFCC2D', '#FF4851', '#4FACFE', '#FF7748']

@Component({
  computed: {
    goal () {
      return this.$store.state.goal
    },
    activities () {
      return this.$store.state.activities || []
    },
    exerciseTags () {
      return this.$store.state.completeExerciseTags || []
    }
  }
})
export class HealthGoals extends Vue {

  protected loading: boolean = true
  // for to add/edit a goal (name)
  protected showAddGoal: boolean = false
  // options and messages are different for the current week goal or for pas weeks
  protected isCurrentWeekGoal: boolean = false

  protected apiClient: ApiClient = new ApiClient()

  protected goal: HealthGoal | null
  // confirm removing the goal
  private showConfirmRemove: boolean = false
  // tooltip to add new KGIs to the current goal
  private showAddKGI: boolean = false
  // popup to edit all the KGIs of the current goal (and add more)
  private showEditKGIs: boolean = false

  private activities: Activity[]
  private exerciseTags: ExerciseTag[]

  constructor () {
    super()
  }

  changeTimeFrame (type, date: string) {
    return this.loadGoalHistory(date)
  }

  showHideAddGoal () {
    this.showAddGoal = !this.showAddGoal
    this.showEditKGIs = false
  }

  showHideEditKgis () {
    this.showEditKGIs = !this.showEditKGIs
  }

  showHideConfirmRemove () {
    this.showConfirmRemove = !this.showConfirmRemove
  }

  showHideAddKGI () {
    this.showAddKGI = !this.showAddKGI
  }

  removeGoal (confirm) {
    this.showHideConfirmRemove()

    if (confirm === true) {
      return this.apiClient.delete(API.TRAINERS_API.HEALTH_GOAL.replace('{memberId}', this.$route.params.id))
        .then(() => {
          this.$store.dispatch('showToast', { message: this.$t('PAGE.MEMBERS.GOALS.GOAL_REMOVED'), fadeOut: true })
          this.$store.dispatch('setGoal', null)
        })
        .catch((error) => console.log('cannot delete goal', error))
    }
  }

  formatKgiText (kgi: HealthIndicator) {
    let value1: any = kgi.target
    let value2: any = kgi.target
    switch (kgi.type) {
      case HealthIndicatorType.ACTIVITY_DURATION:
      case HealthIndicatorType.ACTIVITY_FREQUENCY: {
        const activity = this.activities.find(a => a.id === kgi.sourceId)
        value1 = activity ? this.$t(`ACTIVITIES.${activity.key.toUpperCase()}`) : kgi.sourceId
        value2 = kgi.target
        break
      }
      case HealthIndicatorType.EXERCISE_TAG_PERCENTAGE: {
        const tag = this.exerciseTags.find(t => t.id === kgi.sourceId)
        value1 = tag ? this.$t(`PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.${tag.key.toUpperCase()}`) : kgi.sourceId
        value2 = kgi.target
        break
      }
    }

    return this.$t(`PAGE.MEMBERS.GOALS.KGI_TEXT.${kgi.type.toUpperCase()}`, { value1: value1, value2: value2 })
  }

  render (h: CreateElement) {
    if (this.loading) {
      return
    }

    const totalProgress = this.goal ? this.goal.getProgress() : 0
    let kgisProgress: number[] = []
    let kgis = this.goal ? this.goal.indicators.map((kgi, index) => {
      const progress = kgi.getProgress() || 0
      kgisProgress.push(progress / this.goal!.indicators.length)

      return <div class='kgi-progress'>
        <Subtitle1>{`${index + 1}. ${this.formatKgiText(kgi)}`}</Subtitle1>
        <div class='percentage'>
          <ProgressBar progress={progress} color={PROGRESS_COLOR_SEQUENCE[index % PROGRESS_COLOR_SEQUENCE.length]} />
          {progress === 100 ? <Icon name='util/checked' /> : <Subtitle1 class='percentage-value'>{progress}%</Subtitle1>}
        </div>
        <Spacer />
      </div>
    }) : []

    return <div class='key-health-goal'>
      <H6 class='title row'>
        <span>{this.$t('PAGE.MEMBERS.GOALS.GOAL')}</span>
        <TimeSwitcher dateFilters={[DateFilterType.WEEK_NUMBER]} change={this.changeTimeFrame} align={'right'} allowFutureDate={false} />
      </H6>

      {!this.goal && this.isCurrentWeekGoal ?
        <ErrorScreen title={this.$t('GET_STARTED')} message={this.$t('PAGE.MEMBERS.GOALS.NO_GOAL_INDICATOR')}>
          <Button type='remove' ref='addGoal' label={this.$t('PAGE.MEMBERS.GOALS.ADD_GOAL')} click={this.showHideAddGoal} icon={'util/add'} />
        </ErrorScreen> : null}

      {!this.goal && !this.isCurrentWeekGoal ?
        <ErrorScreen title={this.$t('PAGE.MEMBERS.GOALS.NO_GOAL')} icon={'brand/goal'} /> : null}

      {this.goal ?
        <ContentBox class='health-goal-card'>

          <div class='goal-summary' slot={'bottom'}>
            <H6>{this.goal.isTemplateGoal ? this.$t(`PAGE.MEMBERS.GOALS.${this.goal.key}`) : this.goal.name}</H6>
            {totalProgress === 100 ?
              <div class='percentage'>
                <Subtitle1><Icon name='util/checked' /><span>{this.$t('PAGE.MEMBERS.GOALS.GOAL_DONE')}</span></Subtitle1>
              </div>
              : <div class='percentage'>
                <CombinedProgressBar progress={kgisProgress} color={PROGRESS_COLOR_SEQUENCE} />
                <Subtitle1>{totalProgress}%</Subtitle1>
              </div>}

            {this.renderGoalOptions(h)}
          </div>

          <div class='kgis-progress' slot={'content'}>
            {this.showAddKGI ? <KGITooltip closeCallback={this.showHideAddKGI} /> : null}

            {kgis.length ?
              <Subtitle2 class='kgis-title'>
                <span>{this.$t('PAGE.MEMBERS.GOALS.KEY_HEALTH_GOAL_INDICATORS')}</span>
                {this.isCurrentWeekGoal ?
                  <Button type='remove' ref='addKgiBtn' label={this.$t('PAGE.MEMBERS.GOALS.ADD_KGI')} click={this.showHideAddKGI} icon={'util/add'} /> : null}
              </Subtitle2> : null}

            {kgis.length === 0 ?
              <ErrorScreen title={this.$t('PAGE.MEMBERS.GOALS.NO_KGI')}
                           message={`${this.$t('PAGE.MEMBERS.GOALS.KGI_DESCRIPTION_1')} ${this.$t('PAGE.MEMBERS.GOALS.KGI_DESCRIPTION_2')}`}>
                {this.isCurrentWeekGoal ?
                  <Button type='remove' ref='addKgiBtn' label={this.$t('PAGE.MEMBERS.GOALS.ADD_KGI')} click={this.showHideAddKGI} icon={'util/add'} /> : null}
              </ErrorScreen> : null}

            {kgis}
          </div>
        </ContentBox> : null}

      {this.showAddGoal ? <AddGoal closeCallback={this.showHideAddGoal} /> : null}

      {this.showEditKGIs ? <EditKgis goal={this.goal} closeCallback={this.showHideEditKgis} backCallback={this.showHideAddGoal} /> : null}

      <ConfirmModal show={this.showConfirmRemove} closeCallback={this.removeGoal}
                    message={this.$t('PAGE.MEMBERS.GOALS.REMOVE_GOAL_CONFIRMATION')}
                    type={NotificationTypes.CONFIRM} />
    </div>
  }

  renderGoalOptions (h: CreateElement) {
    return <MenuButton>
      <Popover>
        <OptionGroup>
          <div onClick={this.showHideAddGoal} ref='editGoal'><Subtitle1>{this.$t('PAGE.MEMBERS.GOALS.EDIT_GOAL')}</Subtitle1></div>
          <div onClick={this.showHideConfirmRemove} ref='removeGoal'><Subtitle1>{this.$t('PAGE.MEMBERS.GOALS.REMOVE_GOAL')}</Subtitle1></div>
          <div onClick={this.showHideEditKgis} ref='editKgis'><Subtitle1>{this.$t('PAGE.MEMBERS.GOALS.EDIT_HEALTH_GOAL_INDICATOR')}</Subtitle1></div>
        </OptionGroup>
      </Popover>
    </MenuButton>
  }

  beforeMount (): any {
    return this.loadGoalHistory(convertToYYYYMMDD(new Date()))
  }

  protected loadGoalHistory (date: string) {
    if (date !== 'Invalid date') {
      return new HealthGoalsService().getGoalHistory(this.$route.params.id, date, '')
        .then(goal => {
          this.$store.dispatch('setGoal', goal)
        })
        .catch(() => {
          this.$store.dispatch('setGoal', null)
        })
        .finally(() => {
          this.loading = false
          this.showAddGoal = false
          this.isCurrentWeekGoal = moment().isoWeek() === moment(date, 'YYYY-MM-DD').isoWeek()
        })
    }
  }
}
